import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApolloError } from "src/components/Feedback/ApolloError";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useFlagsRemoteData } from "src/components/Providers/FeatureFlagProvider";
import { OrganizationError, useOrganization } from "src/hooks/useOrganization";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { Home } from "./dashboard";

export function Index() {
  const organizationRD = useOrganization();
  const flagsRD = useFlagsRemoteData(["admin-dashboard"]);

  const renderError = React.useCallback((error: OrganizationError | Error) => {
    if (error instanceof Error) {
      return <GenericError error={error} />;
    }

    switch (error.kind) {
      case "NotFoundError":
        return <NotFound />;
      case "ServerError":
        return <ApolloError error={error.error} />;
    }
  }, []);

  const permissions = useUserPermissions();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  useEffect(() => {
    // Handle page redirect in case admin-dashboard flag is disabled to avoid blank page.
    // Once we admin-dashboard is available for everyone, we can remove this useEffect.
    if (!flagsRD.hasData()) {
      return;
    }

    if (flagsRD.data["admin-dashboard"].enabled) {
      return;
    }

    if (permissions.isLoading) {
      return;
    }

    if (!organizationRD.hasData()) {
      return;
    }

    // no forms available, navigate to "Enrollment periods" page if authorized
    if (permissions.hasOne("navigation.admin.enrollment_periods")) {
      navigate(Url.OrgAdmin.Enrollments.index(organizationRD.data));
      return;
    }

    // or navigate to "Messages" page if authorized
    if (permissions.hasOne("navigation.admin.messages")) {
      navigate(Url.OrgAdmin.Messages.index(organizationRD.data));
      return;
    }

    setLoading(false);
  }, [flagsRD, navigate, organizationRD, permissions]);

  return (
    <RemoteDataView
      remoteData={RD.toTuple(flagsRD, organizationRD)}
      error={renderError}
    >
      {([flags, _]) => {
        const adminDashboardEnabled = flags["admin-dashboard"].enabled;
        if (adminDashboardEnabled) {
          return <Home />;
        }

        if (loading) return <Loading />;

        return null;
      }}
    </RemoteDataView>
  );
}
